import React, { useEffect, useState } from "react";
import { TextInput } from "../../form/inputs";
import ToggleBtn from "../../button/ToggleBtn";
import { useNavigate } from "react-router-dom";
import PrimaryBtn from "../../button/PrimaryBtn";
import { GoogleMap, Marker, Autocomplete } from "@react-google-maps/api";
import { useDispatch, useSelector } from "react-redux";
import {
  addPickUpAddress,
  getAddressDetails,
} from "../../../redux/thunks/setAddressThunk";
import toast from "react-hot-toast";
// HELPER FUNCTION:
const extractAddress = (place) => {
  const address = {
    country: "",
    city: "",
    state: "",
    pincode: "",
    fullAddress: place.formatted_address || "",
    addressLine2: "",
  };

  let sublocality = "";
  let locality = "";
  let route = "";

  place.address_components.forEach((component) => {
    const types = component.types;
    if (types.includes("country")) address.country = component.long_name;
    if (types.includes("administrative_area_level_1"))
      address.province = component.long_name;
    if (types.includes("locality")) {
      address.city = component.long_name;
      locality = component.long_name;
    }
    if (types.includes("sublocality") || types.includes("sublocality_level_1"))
      sublocality = component.long_name;
    if (types.includes("route")) route = component.long_name;
    if (types.includes("postal_code")) address.pincode = component.long_name;
  });

  // 🔥 Short nearby address (priority wise)
  address.addressLine2 =
    sublocality || route || locality || address.province || "";

  return address;
};

const AddAddress = ({ setTab }) => {
  const [isDeliveryEnabled, setIsDeliveryEnabled] = useState(false);
  const dispatch = useDispatch();
  // const {isAddressLoading , isAddressErr} = useSelector((state) => state.pickUpAdd)
  const navigate = useNavigate();
  const handleNav = () => {
    localStorage.removeItem("setup_tab"); // reset flow
    navigate("/dashboard");
  };
  const [address, setAddress] = useState({
    fullAddress: "",
    addressLine2: "",
    country: "",
    city: "",
    state: "",
    pincode: "",
    lat: null,
    lng: null,
  });

  const [deliveryRange, setDeliveryRange] = useState("");
  const [deliverPrice, setDeliveryPrice] = useState("");

  const mapContainerStyle = {
    width: "100%",
    height: "300px",
  };

  const center = {
    lat: 20.5937,
    lng: 78.9629,
  };
  const currencySymbol = localStorage.getItem("currency");
  const [Curr, setCurr] = useState("");
  const [mapCenter, setMapCenter] = useState(center);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [AddressDetail, setAddressDetail] = useState("");
  const autocompleteRef = React.useRef(null);
  const [errors, setErrors] = useState({});
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);
  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    const extracted = extractAddress(place);

    setAddress((prev) => ({
      ...prev,
      ...extracted,
      lat,
      lng,
    }));

    setMapCenter({ lat, lng });
    setMarkerPosition({ lat, lng });
  };

  const handleMapClick = (event) => {
    setIsFetchingAddress(true);
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setMarkerPosition({ lat, lng });
    setMapCenter({ lat, lng });

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        const place = results[0];

        const extracted = extractAddress(place);

        setAddress((prev) => ({
          ...prev,
          ...extracted,
          lat,
          lng,
        }));
      } else {
        toast.error("Unable to fetch address from map");
      }

      setTimeout(() => {
        setIsFetchingAddress(false);
      }, 400);
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    dispatch(getAddressDetails())
      .unwrap()
      .then((res) => {
        const data = res?.data;

        if (!data) return;

        setAddressDetail(data); // optional if you want raw data

        setAddress({
          fullAddress: data.address_1 || "",
          addressLine2: data.address_2 || "",
          country: data.country || "",
          city: data.city || "",
          state: data.province || "",
          pincode: data.pin_code || "",
          lat: data.latitude ? Number(data.latitude) : null,
          lng: data.longitude ? Number(data.longitude) : null,
        });

        // 🗺️ Set map center + marker
        if (data.latitude && data.longitude) {
          const lat = Number(data.latitude);
          const lng = Number(data.longitude);

          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
        }

        // 🚚 Delivery details
        setIsDeliveryEnabled(data.is_home_delivery ? 1 : 0);
        setDeliveryRange(data.delivery_upto || "");
        setDeliveryPrice(data.delivery_charges || "");
      });
  }, [dispatch]);

  const validateFields = () => {
    const newErrors = {};

    if (!address.fullAddress) {
      newErrors.fullAddress = "Please add the address first";
    }
    if (!address.country) {
      newErrors.country = "Please select the country";
    }
    if (!address.city) {
      newErrors.city = "Please add the city first";
    }
    if (!address.state) {
      newErrors.state = "Please add the state first";
    }
    if (!address.pincode) {
      newErrors.pincode = "Please add the pincode first";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onAddressSave = async () => {
    if (!validateFields()) {
      return;
    }
    try {
      const payload = {
        address_1: address.fullAddress,
        address_2: address.addressLine2,
        country: address.country,
        city: address.city,
        province: address.state,
        pin_code: address.pincode,
        latitude: address.lat,
        longitude: address.lng,
        is_home_delivery: isDeliveryEnabled,
        delivery_upto: deliveryRange,
        delivery_charges: deliverPrice,
      };
      // console.log("The payload data is :", payload);

      const res = await dispatch(addPickUpAddress(payload)).unwrap();

      if (res.success) {
        toast.success(res.message || "Address Added Successfully");
        localStorage.removeItem("setup_tab"); // reset flow
        navigate("/dashboard");
      } else {
        toast.error(res.message || "Address Failed");
      }
    } catch (err) {
      toast.error(err?.message || err);
    }
  };

  useEffect(() => {
    const getCountry = address.country?.toLowerCase();

    if (getCountry === "india") {
      setCurr("₹");
    } else {
      setCurr("$");
    }
  }, [address.country]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
      {/* ================= Title ================= */}
      <div className="text-center space-y-2 max-w-2xl mx-auto">
        <h1 className="text-[#191919] text-2xl md:text-3xl font-bold">
          Set up Pickup Information
        </h1>
        <p className="text-gray-600 text-base">
          Add your pickup address and delivery details to make ordering smooth
        </p>
      </div>

      <div className="w-full mt-4">
        <p className="text-[#191919] font-medium">
          Choose location <span className="text-red-500 ml-1">*</span>
        </p>

        <Autocomplete
          onLoad={(ref) => (autocompleteRef.current = ref)}
          onPlaceChanged={onPlaceChanged}
        >
          <div className="relative">
            <input
              type="text"
              placeholder="Search location"
              name="fullAddress"
              value={address.fullAddress}
              onChange={handleChange}
              disabled={isFetchingAddress}
              className="w-full px-4 py-2 mb-3 border rounded-lg outline-none pr-10"
            />

            {isFetchingAddress && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-black rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        </Autocomplete>
      </div>
      {/* ================= Address Form ================= */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
        <TextInput
          label="Address Line 2"
          name="addressLine2"
          value={address.addressLine2}
          onChange={handleChange}
        />

        <div>
          <TextInput
            required={true}
            label="Country"
            name="country"
            value={address.country}
            onChange={handleChange}
          />
          {errors.country && (
            <p className="text-sm text-red-500 mt-1">{errors.country}</p>
          )}
        </div>

        <div>
          <TextInput
            required={true}
            label="City"
            name="city"
            value={address.city}
            onChange={handleChange}
          />
          {errors.city && (
            <p className="text-sm text-red-500 mt-1">{errors.city}</p>
          )}
        </div>

        <div>
          <TextInput
            required={true}
            label="Province / State"
            name="state"
            value={address.state}
            onChange={handleChange}
          />
          {errors.state && (
            <p className="text-sm text-red-500 mt-1">{errors.state}</p>
          )}
        </div>

        <div>
          <TextInput
            required={true}
            label="Pin Code"
            name="pincode"
            value={address.pincode}
            onChange={handleChange}
          />
          {errors.pincode && (
            <p className="text-sm text-red-500 mt-1">{errors.pincode}</p>
          )}
        </div>
      </div>

      {/* ================= Google Map ================= */}
      <div className="space-y-2">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={15}
          center={mapCenter}
          onClick={handleMapClick}
        >
          {markerPosition && <Marker position={markerPosition} />}
        </GoogleMap>
      </div>

      {/* ================= Delivery Toggle ================= */}
      <div className="flex items-center justify-between max-w-md">
        <p className="text-base text-[#191919] font-medium">
          Do you offer home delivery?
        </p>
        <ToggleBtn
          isOn={isDeliveryEnabled === 1}
          size={40}
          onChange={(val) => setIsDeliveryEnabled(val ? 1 : 0)}
        />
      </div>

      {/* ================= Delivery Options (Conditional) ================= */}
      {isDeliveryEnabled ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ">
          {/* Deliver Range */}
          <div className="border border-[#E5E5E5] rounded-xl p-4 bg-gray-50 space-y-2">
            <p className="text-sm font-medium text-[#191919]">Deliver up to</p>
            <div className="flex items-center">
              <input
                type="number"
                placeholder="10"
                value={deliveryRange}
                onChange={(e) => setDeliveryRange(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-l-md outline-none text-sm"
              />
              <span className="px-3 py-2 bg-[#FFEFEB] border border-l-0 border-[#FFCCBF] rounded-r-md text-sm">
                KM
              </span>
            </div>
          </div>

          {/* Delivery Price */}
          <div className="border border-[#E5E5E5] rounded-xl p-4 bg-gray-50 space-y-2">
            <p className="text-sm font-medium text-[#191919]">Delivery Price</p>
            <div className="flex items-center">
              <span className="px-3 py-2 bg-[#FFEFEB] border border-r-0 border-[#FFCCBF] rounded-l-md text-sm">
                {currencySymbol || Curr}
              </span>
              <input
                type="number"
                value={deliverPrice}
                onChange={(e) => setDeliveryPrice(e.target.value)}
                placeholder="5"
                className="flex-1 px-3 py-2 border rounded-r-md outline-none text-sm"
              />
            </div>
          </div>
        </div>
      ) : (
        ""
      )}

      <div className="flex flex-col-reverse px-10 sm:flex-row sm:items-center md:justify-between gap-4 pt-6 mt-8 max-w-7xl mx-auto">
        <PrimaryBtn
          btnName="Continue"
          clickEvent={onAddressSave}
          extraClass="w-full sm:w-full px-10"
        />
      </div>
    </div>
  );
};

export default AddAddress;
